import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useVoiceRecorder } from '../hooks/useVoiceRecorder';
import { useUpload, useCreateJob, useJobStatus, useProcessText, useQuote } from '../api/hooks';
import { useOfflineQueue } from '../hooks/useOfflineQueue';
import { ApiError } from '../api/types';
import type { ProcessQuoteResponse } from '../api/types';
import { Button } from '@/components/ui/button';
import { RecordButton } from './RecordButton';
import { StatusIndicator } from './StatusIndicator';
import { QuoteResult } from './QuoteResult';
import { PendingBadge } from './PendingBadge';
import { TextPromptInput } from './TextPromptInput';

interface QuoteFlowProps {
  selectedQuoteId: string | null;
  onQuoteLoaded: () => void;
  onActiveChange?: (active: boolean) => void;
}

export function QuoteFlow({
  selectedQuoteId,
  onQuoteLoaded,
  onActiveChange,
}: Readonly<QuoteFlowProps>) {
  const { t, i18n } = useTranslation();
  const { getAccessToken } = useKindeAuth();
  const queryClient = useQueryClient();
  const recorder = useVoiceRecorder();
  const upload = useUpload(getAccessToken as () => Promise<string>);
  const createJob = useCreateJob(getAccessToken as () => Promise<string>);
  const processText = useProcessText(getAccessToken as () => Promise<string>);
  const selectedQuote = useQuote(getAccessToken as () => Promise<string>, selectedQuoteId);
  const { pendingCount, isSyncing, queueRecording } = useOfflineQueue();
  const [loadedQuote, setLoadedQuote] = useState<ProcessQuoteResponse | null>(null);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);

  const jobStatus = useJobStatus(getAccessToken as () => Promise<string>, activeJobId);

  // Load quote when job completes
  const completedQuoteId = jobStatus.data?.status === 'completed' ? jobStatus.data.quote_id : null;
  const completedQuote = useQuote(getAccessToken as () => Promise<string>, completedQuoteId);

  const prevUploadError = useRef(false);
  const prevCreateJobError = useRef(false);
  const prevProcessTextError = useRef(false);
  const prevSelectedQuoteError = useRef(false);
  const prevJobFailed = useRef(false);

  useEffect(() => {
    if (upload.isError && !prevUploadError.current) {
      const key = upload.error instanceof ApiError ? upload.error.errorKey : 'uploadFailed';
      toast.error(t(`error.${key}`));
    }
    prevUploadError.current = upload.isError;
  }, [upload.isError, upload.error, t]);

  useEffect(() => {
    if (createJob.isError && !prevCreateJobError.current) {
      const key = createJob.error instanceof ApiError ? createJob.error.errorKey : 'uploadFailed';
      toast.error(t(`error.${key}`));
    }
    prevCreateJobError.current = createJob.isError;
  }, [createJob.isError, createJob.error, t]);

  useEffect(() => {
    if (processText.isError && !prevProcessTextError.current) {
      const key =
        processText.error instanceof ApiError ? processText.error.errorKey : 'uploadFailed';
      toast.error(t(`error.${key}`));
    }
    prevProcessTextError.current = processText.isError;
  }, [processText.isError, processText.error, t]);

  useEffect(() => {
    if (selectedQuote.isError && !prevSelectedQuoteError.current) {
      toast.error(t('error.loadQuoteFailed'));
    }
    prevSelectedQuoteError.current = selectedQuote.isError;
  }, [selectedQuote.isError, t]);

  // Handle job failure
  useEffect(() => {
    const isFailed = jobStatus.data?.status === 'failed';
    if (isFailed && !prevJobFailed.current) {
      const msg = jobStatus.data?.error_message || t('error.uploadFailed');
      toast.error(msg);
    }
    prevJobFailed.current = !!isFailed;
  }, [jobStatus.data?.status, jobStatus.data?.error_message, t]);

  useEffect(() => {
    if (selectedQuote.isSuccess && selectedQuote.data) {
      setLoadedQuote(selectedQuote.data);
      onQuoteLoaded();
    }
  }, [selectedQuote.isSuccess, selectedQuote.data, onQuoteLoaded]);

  // Handle completed job — load the quote and show it
  useEffect(() => {
    if (completedQuote.isSuccess && completedQuote.data) {
      setLoadedQuote(completedQuote.data);
      setActiveJobId(null);
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
    }
  }, [completedQuote.isSuccess, completedQuote.data, queryClient]);

  useEffect(() => {
    const jobDone = completedQuote.isSuccess && completedQuote.data;
    if (processText.isSuccess || loadedQuote || jobDone) {
      onActiveChange?.(true);
    }
  }, [
    processText.isSuccess,
    loadedQuote,
    completedQuote.isSuccess,
    completedQuote.data,
    onActiveChange,
  ]);

  const handleStart = useCallback(() => {
    recorder.start();
  }, [recorder]);

  const handleStop = useCallback(async () => {
    const { blob, mimeType } = await recorder.stop();
    const language = i18n.language.split('-')[0];
    upload.mutate(
      { blob, mimeType },
      {
        onSuccess: (response) => {
          createJob.mutate(
            { file_key: response.key, language },
            {
              onSuccess: (jobResponse) => {
                setActiveJobId(jobResponse.id);
              },
            },
          );
        },
        onError: (error) => {
          /* istanbul ignore else -- @preserve: non-network errors handled by upload.isError effect */
          if (error instanceof TypeError) {
            queueRecording(blob, mimeType, language).catch(() => {
              toast.error(t('offline.queueFailed'));
            });
          }
        },
      },
    );
  }, [recorder, upload, createJob, queueRecording, i18n.language, t]);

  const handleTextSubmit = useCallback(
    (text: string) => {
      const language = i18n.language.split('-')[0];
      processText.mutate(
        { text, language },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['quotes'] });
          },
        },
      );
    },
    [processText, queryClient, i18n.language],
  );

  const handleReset = useCallback(() => {
    upload.reset();
    createJob.reset();
    processText.reset();
    setLoadedQuote(null);
    setActiveJobId(null);
    onActiveChange?.(false);
  }, [upload, createJob, processText, onActiveChange]);

  // Determine if a job is actively processing
  const isJobActive =
    activeJobId !== null &&
    jobStatus.data?.status !== 'failed' &&
    jobStatus.data?.status !== 'completed';

  // Determine current status for StatusIndicator
  const getStatus = (): 'recording' | 'uploading' | 'processing' | 'done' | null => {
    if (recorder.state === 'recording') return 'recording';
    if (upload.isPending) return 'uploading';
    if (
      createJob.isPending ||
      isJobActive ||
      processText.isPending ||
      selectedQuote.isFetching ||
      completedQuote.isFetching
    ) {
      return 'processing';
    }
    if (processText.isSuccess || loadedQuote) return 'done';
    return null;
  };

  // Determine RecordButton state
  const getRecordState = (): 'idle' | 'recording' | 'disabled' => {
    if (upload.isPending || createJob.isPending || isJobActive || processText.isPending)
      return 'disabled';
    if (recorder.state === 'recording') return 'recording';
    return 'idle';
  };

  const jobFailed = jobStatus.data?.status === 'failed';
  const showResult = processText.isSuccess || loadedQuote;
  const hasError = upload.isError || createJob.isError || processText.isError || jobFailed;

  return (
    <div className="space-y-4">
      <PendingBadge count={pendingCount} isSyncing={isSyncing} />

      {!showResult && (
        <>
          <p className="text-center text-sm text-muted-foreground">{t('quoteFlow.hint')}</p>
          <RecordButton state={getRecordState()} onStart={handleStart} onStop={handleStop} />

          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-foreground/70">{t('textPrompt.or')}</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <TextPromptInput onSubmit={handleTextSubmit} isPending={processText.isPending} />
        </>
      )}

      <StatusIndicator status={getStatus()} />

      {/* Microphone error stays inline */}
      {recorder.error && (
        <div
          role="alert"
          className="rounded-lg bg-destructive/10 p-3 text-center text-sm text-destructive"
        >
          <p>{t(`error.${recorder.error}`)}</p>
        </div>
      )}

      {/* Retry button for transient errors (upload/process/job failures) */}
      {hasError && !recorder.error && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            onClick={handleReset}
            aria-label={t('error.retry')}
            className="touch-target"
          >
            {t('error.retry')}
          </Button>
        </div>
      )}

      {processText.isSuccess && processText.data && !loadedQuote && (
        <div className="space-y-4">
          <QuoteResult data={processText.data} />
          <div className="flex justify-center">
            <Button
              variant="outline"
              onClick={handleReset}
              aria-label={t('record.again')}
              className="touch-target"
            >
              {t('record.again')}
            </Button>
          </div>
        </div>
      )}

      {loadedQuote && (
        <div className="space-y-4">
          <QuoteResult data={loadedQuote} />
          <div className="flex justify-center">
            <Button
              variant="outline"
              onClick={handleReset}
              aria-label={t('record.again')}
              className="touch-target"
            >
              {t('record.again')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
